import {
  Clv1AdViewedProperties,
  OrderDetailInstallmentsEditedProperties,
  PreActionsProperties,
  PreConfirmationProperties,
  PreFormEditedProperties,
  PreFormReviewedProperties,
  PreInstallmentsEditedProperties,
  ScreenViewedProperties,
  TsbFinancialFilterChangedProperties,
  TsbWebsiteAdClickEventProperties,
} from 'core/ampli';

export interface AmplitudeTrackActions {
  // Tracking the org and page viewed when a user clicks the TSB help link
  tsbHelpLinkClicked: (props: {
    organization_id: string;
    organization_name: string;
    page_viewed_url: string;
  }) => void;
  // Tracking which org, program, and season the user is viewing when they use the registration wizard
  registrationWizardViewed: (props: {
    organization_id: string;
    organization_name: string;
    program_id: string;
    season_id: string;
  }) => void;
  // Tracks when the user clicks on registration actions on the registration page
  tsbRegistrationActionClicked: (props: {
    registration_action: AMPLITUDE_REGISTRATION_ACTIONS;
  }) => void;
  // Tracks which kind of registration is exported by the user
  tsbRegistrationExported: (props: {
    registration_export_type: AMPLITUDE_REGISTRATION_EXPORT_TYPE;
  }) => void;
  // Tracks which page pathname the user is viewing
  screenViewed: (path: string) => void;
  // called manually and not tied to a url change, ex modal or drawer.
  surfaceViewed: (screenName: ScreenViewedProperties['screen_name']) => void;
  // Tracks when the user changes the date range in the financials report
  tsbFinancialDateRangeChanged: (props: {
    financial_date_option: AMPLITUDE_FINANCIAL_DATE_OPTION;
    financial_report: AMPLITUDE_REPORT_TYPE;
  }) => void;
  // Tracks when the user clicks on a financial action on the financials page
  tsbFinancialActionActionClicked: (props: {
    financial_action: AMPLITUDE_FINANCIAL_ACTIONS;
    financial_report: AMPLITUDE_REPORT_TYPE;
  }) => void;
  // Tracking when the user exports a financial report
  tsbFinancialsExported: (financialReport: AMPLITUDE_REPORT_TYPE) => void;
  // Tracking which filter is changed in the financials report
  tsbFinancialFilterChanged: (
    financialFilter: TsbFinancialFilterChangedProperties
  ) => void;
  advertisementViewed: (props: {
    ad: Clv1AdViewedProperties['ad_type'];
  }) => void;
  advertisementClicked: (props: {
    ad: Clv1AdViewedProperties['ad_type'];
  }) => void;
  tsbAdClickAction: (props: {
    adType: AMPLITUDE_TSB_AD_TYPE;
    upsell_action: TsbWebsiteAdClickEventProperties['ad_page_action'];
  }) => void;
  preFormEdited: (editType: PreFormEditedProperties['pre_edit']) => void;
  preActions: (pre_action: PreActionsProperties['pre_action']) => void;
  preFormReviewed: (
    formReviewed: PreFormReviewedProperties['review_type']
  ) => void;
  preInstallmentsEdited: (editType: InstallmentsEditType) => void;
  preConfimation: (
    action: PreConfirmationProperties['pre_confirmation_action']
  ) => void;
  // Order adjustments
  adjustOrderActionClicked: (action: ORDER_ADJUSTMENT_ACTION) => void;
  adjustOrderInstallmentsEdited: (
    action: ORDER_ADJUSTMENT_INSTALLMENT_ACTION
  ) => void;
  adjustOrderBalanceChanged: (action: PRE_FORM_REVIEWED) => void;
}

export enum AMPLITUDE_SCREEN_NAMES {
  PROGRAM_INDEX = 'Org Programs List',
  PROGRAM_SEASONS = 'Program Seasons List',
  PROGRAM_REPORT = 'Org Programs Report',
  CALENDAR = 'Org Calendar',
  MEMBER_INDEX = 'Org Member Directory',
  MEMBER = 'Org Member Profile',
  STAFF_INDEX = 'Org Staff List',
  SETTINGS_INDEX = 'Org Settings General',
  MERCHANT_ACCOUNTS = 'Merchant Accounts List',
  MESSAGE_INDEX = 'Org Messages List',
  MESSAGE_NEW = 'Org New Message',
  PARTICIPANTS_INDEX = 'Season Participants List',
  FINANCIALS_DEPOSITS = 'Org Financials: Deposits',
  FINANCIALS_OUTSTANDING = 'Org Financials: Outstanding',
  FINANCIALS_TRANSACTIONS = 'Org Financials: Transactions',
  PARTICIPANT = 'Season Member Profile',
  REGISTRATION_INDEX = 'Org Registration List',
  REGISTRATION_DETAILS = 'Reg 2: Registration Report',
  SEASON_REGISTRATIONS_INDEX = 'Season Registration List',
  SEASON_FINANCIALS = 'Season Financial Settings',
  REGISTRATION_PROGRAM_SELECT = 'Reg 2: Setup Wizard - Select Program',
  MEMBER_PROFILE_PARTICIPATION = 'Member Profile: Participation',
  MEMBER_PROFILE_HISTORY = 'Member Profile: History',
  MEMBER_PROFILE_INVOICES = 'Member Profile: Invoices',
  MEMBER_PROFILE_REGISTRATIONS = 'Member Profile: Registrations',
  WEBSITES = 'TSB Apps: Websites',
  COACHES_PACK = 'TSB Apps: Coaches Pack',
  TOURNAMENTS = 'TSB Apps: Tournaments',
  SPONSORSHIPS = 'TSB Apps: Sponsorships',
  PRE_EDIT = 'PRE Edit',
  PRE_REVIEW = 'PRE Review',
  ORDER_DETAILS_ADJUSTMENT = 'Order Detail Adjustment',
}

export enum AMPLITUDE_FINANCIAL_DATE_OPTION {
  TODAY = 'Today',
  LAST_7_DAYS = 'Last 7 Days',
  LAST_14_DAYS = 'Last 14 Days',
  LAST_30_DAYS = 'Last 30 Days',
  MONTH_TO_DATE = 'Month to Date',
  YEAR_TO_DATE = 'Year to Date',
  CUSTOM = 'Custom',
}

// The registration actions for each row in the registration table
export enum AMPLITUDE_REGISTRATION_ACTIONS {
  EMAIL = 'Individual Action: Email',
  READY_TO_ROSTER = 'Individual Action: Ready to Roster',
  ORDER_DETAILS = 'Individual Action: Order Details',
  ROSTER_PROFILE = 'Individual Action: Roster Profile',
  MANAGE_COLUMNS = 'Manage Columns',
  COPY_FORM_LINK = 'Copy Form Link',
  SHARE_FORM = 'Share Form',
  EDIT_FORM = 'Edit Form',
  GO_TO_ROSTERING = 'Go to Rostering',
  BULK_EXPORT = 'Bulk Action: Export Selected',
  BULK_READY_TO_ROSTER = 'Bulk Action: Ready to Roster',
  BULK_EMAIL = 'Bulk Action: Email Selected',
  BULK_EMAIL_ALL = 'Bulk Action: Email All',
  BULK_DELETE = 'Bulk Action: Delete Selected',
  BULK_ACCEPT = 'Bulk Action: Accept from Waitlist',
  BULK_RELEASE = 'Bulk Action: Release from Waitlist',
  EDIT_REGISTRATION = 'Edit Registration',
  VIEW_LOGS = 'View Audit Log',
}

export enum AMPLITUDE_FINANCIAL_ACTIONS {
  VIEW_TRANSACTIONS = 'View Transactions',
  VIEW_REGISTRATION = 'View Registration',
  VIEW_ORDER = 'View Order',
  VIEW_BATCH = 'View Batch',
  VIEW_INVOICE = 'View Invoice',
}

export enum AMPLITUDE_REPORT_TYPE {
  TRANSACTIONS = 'Transactions',
  OUTSTANDING = 'Outstanding',
  DEPOSIT = 'Deposit',
}

export enum FINANCIAL_FILTERS {
  MEMBER_NAME = 'Member Name',
  PROGRAM = 'Program',
  SOURCE_TYPE = 'Source Type',
  REGISTRATION = 'Registration',
  INVOICE = 'Invoice',
  STATUS = 'Status',
  PAYMENT_TYPE = 'Payment Type',
  DEPOSIT_ACCOUNT = 'Deposit Account',
  CLEAR_ALL = 'Clear All',
}

export enum AMPLITUDE_REGISTRATION_EXPORT_TYPE {
  REGISTRATION_DATA = 'Reg 2: Registration Data',
  WAIVERS = 'Reg 2: Waivers',
  FEE_SUMMARY = 'Reg 2: Fee Summary',
  FEE_DETAILS = 'Reg 2: Fee Detail',
}

export enum AMPLITUDE_TSB_AD_TYPE {
  WEBSITE = 'website',
  TOURNAMENT = 'tournament',
  SPONSORSHIP = 'sponsorship',
  COACHES_PACK = 'coaches_pack',
}

export enum AMPLITUDE_PRE_ACTIONS {
  REVIEW_UPDATES_CLICKED = 'Review Updates Clicked',
  CANCEL_CLICKED = 'Cancel Clicked',
  CONSENT_DIALOGUE_CLICKED = 'Consent Dialogue Clicked',
  BACK_TO_EDITING_CLICKED = 'Back to editing Clicked',
  SAVE_CHANGES_CLICKED = 'Save changes Clicked',
}

export enum PRE_FORM_REVIEWED {
  BALANCE_INCREASE = 'Balance change increased',
  BALANCE_DECREASE = 'Balance change decreased',
  NO_BALANCE_CHANGE = 'No balance change',
}

export enum INSTALLMENTS_EDIT_TYPE {
  COLLECT_ONLINE = 'Collect Online',
  COLLECT_OFFLINE = 'Collect Offline',
  INSTALLMENT_ADDED = 'Installment added',
  INSTALLMENT_REMOVED = 'Installment removed',
  INSTALLMENT_AMOUNT_CHANGED = 'Installment amount changed',
  INSTALLMENT_DATE_CHANGED = 'Installment date changed',
}
export type InstallmentsEditType =
  | PreInstallmentsEditedProperties['installment_edit_type']
  | OrderDetailInstallmentsEditedProperties['installment_edit_type'];

export enum AMPLITUDE_PRE_CONFIRMATION_ACTION {
  VIEW_ORDER = 'View Order',
  CLOSE = 'Close',
}
export enum AMPLITUDE_PRE_EDIT {
  REGISTRATION_OPTION = 'Registration option',
  FEE_RELATED_QUESTION = 'Fee related question',
  ADDITIONAL_QUESTION = 'Additional question',
  DOCUMENT_UPLOADED = 'Document Uploaded',
  DOCUMENT_REPLACED = 'Document Replaced',
  DOCUMENT_DELETED = 'Document Deleted',
  DOCUMENT_VIEWED = 'Document Viewed',
}

export enum ORDER_ADJUSTMENT_ACTION {
  CANCEL = 'Cancel Clicked',
  SAVE = 'Save Clicked',
}

export enum ORDER_ADJUSTMENT_INSTALLMENT_ACTION {
  ADDED = 'Installment added',
  REMOVED = 'Installment removed',
  AMOUNT_CHANGED = 'Installment amount changed',
  DATE_CHANGED = 'Installment date changed',
}
